import { getFileActions } from "../../stores/fileStore";
import { getGraspActions } from "../../stores/graspStore";
import { usePanelStore } from "../../stores/panelStore";
import { getSceneState } from "../../stores/sceneStore";
import {
  addDefaultBody,
  addDefaultCollisionGeom,
  addDefaultJoint,
  addDefaultSite,
  addDefaultVisualGeom,
} from "../../utils/bodyAddActions";
import { findBodyForJointUuid } from "../../utils/bodyHierarchy";
import {
  addDefaultBodyConstraint,
  addDefaultJointConstraint,
  addDefaultTendon,
} from "../../utils/constraintAddActions";
import { focusNewEntityInInspector } from "../../utils/focusNewEntityInInspector";
import { getJointTypeDisabledReason } from "../../utils/jointTypeValidation";
import { nextAvailableCameraName } from "../../utils/nextAvailableCameraName";
import { rememberAutoParentForSelectedItem } from "../../utils/pendingAutoParentDeletion";
import {
  collisionGeomSelectionKeyAfterDuplicate,
  duplicateCollisionGeomFromKey,
  runDuplicateBodySubtreeGrouped,
} from "../../utils/sceneDuplicateCommands";
import { dispatchViewportCameraVisibility } from "../../components/draggable-panels/scene-inventory/sceneInventoryViewportEvents";
import { EMPTY_OBJECT } from "../scene/sceneConstants";
import { findSiteObjectInViewerScene } from "../scene/sceneObjectLookups";

export function useSceneContextMenuActions({
  allEqualities,
  allSites,
  allTendons,
  isPhysicsRunning,
  resolveLibraryDropPointFromClientPoint,
  safeBodies,
  safeJointMapObject,
  sceneActions,
  sceneContextMenuData,
  sceneObjectsRef,
  sceneRef,
  selectionActions,
  setActiveSubmenu,
  setSceneAddGeomKindSubmenuOpen,
  setSceneAddJointKindSubmenuOpen,
  setSceneAddVisualGeomKindSubmenuOpen,
  setSceneContextMenuOpen,
  worldRootTargetBodyUuid,
}) {
  const closeContextMenu = () => {
    setActiveSubmenu(null);
    setSceneAddJointKindSubmenuOpen(false);
    setSceneAddGeomKindSubmenuOpen(false);
    setSceneAddVisualGeomKindSubmenuOpen(false);
    setSceneContextMenuOpen(false);
  };

  const resolveMeshUuidForDeletion = (candidate) => {
    const key = candidate?.uuid || candidate?.name || null;
    if (!key) return null;
    const state = getSceneState();
    const meshes = state?.meshes || EMPTY_OBJECT;
    if (meshes[key]) return key;
    const byName = Object.values(meshes).find((mesh) => mesh?.name === key);
    return byName?.uuid || null;
  };

  const resolveSiteUuidForDeletion = (candidate) => {
    const key = candidate?.uuid || candidate?.name || null;
    if (!key) return null;
    const state = getSceneState();
    const sites = state?.sites || EMPTY_OBJECT;
    if (sites[key]) return key;
    const byName = Object.values(sites).find((site) => site?.name === key);
    return byName?.uuid || null;
  };

  const resolveGeomTargetForDeletion = (candidate) => {
    const key = candidate?.name || candidate?.uuid || null;
    if (!key || typeof key !== "string") return null;
    const sepIdx = key.indexOf(":");
    if (sepIdx <= 0 || sepIdx >= key.length - 1) return null;
    return { bodyUuid: key.slice(0, sepIdx), geomUuid: key.slice(sepIdx + 1) };
  };

  const resolveInertialBodyUuidForDeletion = (candidate) => {
    const key = candidate?.name || candidate?.uuid || null;
    if (typeof key === "string") {
      const sepIdx = key.indexOf(":");
      if (sepIdx > 0) {
        return key.slice(0, sepIdx);
      }
    }
    return sceneContextMenuData.owningBodyUuid || null;
  };

  const handleContextDeleteSelect = (targetType) => {
    if (isPhysicsRunning) return;
    const candidate = sceneContextMenuData.candidates[targetType];
    if (!candidate) return;

    switch (targetType) {
      case "object":
        if (candidate.uuid) {
          sceneActions.removeBodyObject(candidate.uuid);
        } else {
          return;
        }
        break;
      case "body":
        if (candidate.uuid) {
          sceneActions.removeBody(candidate.uuid);
        } else {
          return;
        }
        break;
      case "mesh": {
        if (candidate.selectionType === "geom") {
          const target = resolveGeomTargetForDeletion(candidate);
          if (!target) return;
          sceneActions.removeGeom(target.bodyUuid, target.geomUuid);
          break;
        }
        const meshUuid = resolveMeshUuidForDeletion(candidate);
        if (!meshUuid) return;
        sceneActions.removeMesh(meshUuid);
        break;
      }
      case "geom": {
        const target = resolveGeomTargetForDeletion(candidate);
        if (!target) return;
        sceneActions.removeGeom(target.bodyUuid, target.geomUuid);
        break;
      }
      case "inertial": {
        const bodyUuid = resolveInertialBodyUuidForDeletion(candidate);
        if (!bodyUuid) return;
        sceneActions.removeInertia(bodyUuid);
        break;
      }
      case "joint":
        if (candidate.uuid) {
          sceneActions.removeJoint(candidate.uuid);
        } else {
          return;
        }
        break;
      case "site": {
        const siteUuid = resolveSiteUuidForDeletion(candidate);
        if (!siteUuid) return;
        sceneActions.removeSite(siteUuid);
        break;
      }
      case "camera":
        if (candidate.uuid) {
          sceneActions.removeCamera(candidate.uuid);
        } else {
          return;
        }
        break;
      default:
        return;
    }

    selectionActions.clearAllSelections();
    setActiveSubmenu(null);
    setSceneContextMenuOpen(false);
  };

  const handleContextClearScene = () => {
    if (isPhysicsRunning) return;

    sceneActions.resetScene({ preserveTerrain: true });
    getFileActions().clearAllKeyframes();
    selectionActions.clearAllSelections();
    selectionActions.clearAllHovers();
    usePanelStore.getState().actions.setExpandedBodies({});
    getGraspActions().clearAllGrasps();

    setActiveSubmenu(null);
    setSceneContextMenuOpen(false);
  };

  const handleContextDuplicateCollision = (geomKey) => {
    if (isPhysicsRunning || !geomKey) return;
    const newUuid = duplicateCollisionGeomFromKey(sceneActions, safeBodies, geomKey);
    const selectionKey = collisionGeomSelectionKeyAfterDuplicate(geomKey, newUuid);
    if (selectionKey) {
      focusNewEntityInInspector("scene", () => selectionActions.selectGeom(selectionKey), {
        duplicateChromeFlash: true,
      });
    }
    setActiveSubmenu(null);
    setSceneContextMenuOpen(false);
  };

  const focusNewContextEntity = (selectAction, value) => {
    if (value == null || value === "" || typeof selectAction !== "function") return;
    focusNewEntityInInspector("scene", () => selectAction(value));
  };

  const resolveContextPlacementPosition = (parentBodyUuid = null) => {
    const placement = resolveLibraryDropPointFromClientPoint(sceneContextMenuData.clientPoint);
    if (!placement?.modelPoint) return [0, 0, 0];
    if (!parentBodyUuid) return placement.modelPoint;

    const parentObj = sceneObjectsRef.current?.[parentBodyUuid] || null;
    if (!parentObj || !placement.worldPoint) return placement.modelPoint;
    parentObj.updateWorldMatrix(true, false);
    const parentLocalPoint = placement.worldPoint.clone();
    parentObj.worldToLocal(parentLocalPoint);
    return [parentLocalPoint.x, parentLocalPoint.y, parentLocalPoint.z];
  };

  const resolveContextPlacementPositionForObject = (object) => {
    const placement = resolveLibraryDropPointFromClientPoint(sceneContextMenuData.clientPoint);
    if (!placement?.worldPoint || !object) return [0, 0, 0];
    object.updateWorldMatrix(true, false);
    const localPoint = placement.worldPoint.clone();
    object.worldToLocal(localPoint);
    return [localPoint.x, localPoint.y, localPoint.z];
  };

  const handleContextAddBody = (parentBodyUuid = null) => {
    if (isPhysicsRunning) return;
    const position = resolveContextPlacementPosition(parentBodyUuid);
    const addedBodyUuid = addDefaultBody(sceneActions, parentBodyUuid, position);
    focusNewContextEntity(selectionActions.selectBody, addedBodyUuid);
    closeContextMenu();
  };

  const handleContextAddJointToBody = (parentBodyUuid, kind, options = {}) => {
    if (isPhysicsRunning || !kind) return;
    const forceNewRoot = options.forceNewRoot === true;
    let parent = parentBodyUuid || null;
    let createdAutoParent = false;
    if (!parent) {
      if (!forceNewRoot && worldRootTargetBodyUuid) {
        parent = worldRootTargetBodyUuid;
      } else {
        const position = resolveContextPlacementPosition(null);
        parent = addDefaultBody(sceneActions, null, position, {
          addFreeJoint: kind === "free",
        });
        createdAutoParent = Boolean(parent);
      }
    }
    if (!parent) return;
    if (createdAutoParent && kind === "free") {
      const addedJointUuid = getSceneState()?.bodies?.[parent]?.joints?.[0] || null;
      if (addedJointUuid) {
        focusNewEntityInInspector("sceneTreeObject", () => selectionActions.selectBody(parent));
      }
      closeContextMenu();
      return;
    }
    const disabledReason = getJointTypeDisabledReason({
      kind,
      bodies: safeBodies,
      joints: safeJointMapObject,
      bodyUuid: parent,
    });
    if (disabledReason) {
      setSceneAddJointKindSubmenuOpen(true);
      setSceneAddGeomKindSubmenuOpen(false);
      setSceneAddVisualGeomKindSubmenuOpen(false);
      return;
    }
    const addedJointUuid = addDefaultJoint(sceneActions, parent, kind);
    if (addedJointUuid) {
      if (createdAutoParent) {
        rememberAutoParentForSelectedItem({
          itemType: "joint",
          itemUuid: addedJointUuid,
          parentBodyUuid: parent,
        });
      }
      focusNewContextEntity(selectionActions.selectJoint, addedJointUuid);
    }
    closeContextMenu();
  };

  const handleContextAddSiteToBody = (parentBodyUuid) => {
    if (isPhysicsRunning) return;
    const position = resolveContextPlacementPosition(parentBodyUuid ?? null);
    const addedSiteUuid = addDefaultSite(sceneActions, parentBodyUuid ?? null, position);
    if (!addedSiteUuid) return;
    focusNewContextEntity(selectionActions.selectSite, addedSiteUuid);
    closeContextMenu();
  };

  const handleContextAddGeomToBody = (parentBodyUuid, kind, options = {}) => {
    if (isPhysicsRunning || !kind) return;
    const forceNewRoot = options.forceNewRoot === true;
    let parent = parentBodyUuid || null;
    let createdAutoParent = false;
    if (!parent) {
      if (!forceNewRoot && worldRootTargetBodyUuid) {
        parent = worldRootTargetBodyUuid;
      } else {
        const position = resolveContextPlacementPosition(null);
        parent = addDefaultBody(sceneActions, null, position);
        createdAutoParent = Boolean(parent);
      }
    }
    if (!parent) return;
    const addedGeomUuid = addDefaultCollisionGeom(sceneActions, parent, kind);
    if (addedGeomUuid) {
      if (createdAutoParent) {
        rememberAutoParentForSelectedItem({
          itemType: "geom",
          itemUuid: addedGeomUuid,
          parentBodyUuid: parent,
        });
      }
      focusNewContextEntity(selectionActions.selectGeom, `${parent}:${addedGeomUuid}`);
    }
    closeContextMenu();
  };

  const handleContextAddVisualGeomToBody = (parentBodyUuid, kind, options = {}) => {
    if (isPhysicsRunning || !kind) return;
    const forceNewRoot = options.forceNewRoot === true;
    let parent = parentBodyUuid || null;
    let createdAutoParent = false;
    if (!parent) {
      if (!forceNewRoot && worldRootTargetBodyUuid) {
        parent = worldRootTargetBodyUuid;
      } else {
        const position = resolveContextPlacementPosition(null);
        parent = addDefaultBody(sceneActions, null, position);
        createdAutoParent = Boolean(parent);
      }
    }
    if (!parent) return;
    if (kind === "mesh") {
      window.dispatchEvent(
        new CustomEvent("start-visual-mesh-creation", {
          detail: { parentBodyUuid: parent },
        })
      );
      closeContextMenu();
      return;
    }
    const addedGeomUuid = addDefaultVisualGeom(sceneActions, parent, kind);
    if (addedGeomUuid) {
      if (createdAutoParent) {
        rememberAutoParentForSelectedItem({
          itemType: "geom",
          itemUuid: addedGeomUuid,
          parentBodyUuid: parent,
        });
      }
      focusNewContextEntity(selectionActions.selectGeom, `${parent}:${addedGeomUuid}`);
    }
    closeContextMenu();
  };

  const handleContextAddConstraint = (targetBodyUuid = null, targetJointUuid = null) => {
    if (isPhysicsRunning) return;
    const addedEqualityUuid = targetJointUuid
      ? addDefaultJointConstraint(
          sceneActions,
          safeJointMapObject || {},
          targetJointUuid,
          allEqualities || {}
        )
      : addDefaultBodyConstraint(
          sceneActions,
          safeBodies || {},
          targetBodyUuid,
          "connect",
          allEqualities || {}
        );
    focusNewContextEntity(selectionActions.selectEquality, addedEqualityUuid);
    closeContextMenu();
  };

  const handleContextAddTendon = (targetJointUuid = null) => {
    if (isPhysicsRunning) return;
    const addedTendonUuid = addDefaultTendon(
      sceneActions,
      safeJointMapObject || {},
      targetJointUuid,
      allTendons || {}
    );
    focusNewContextEntity(selectionActions.selectTendon, addedTendonUuid);
    closeContextMenu();
  };

  const handleContextAddFixedWorldCamera = () => {
    if (isPhysicsRunning) return;
    const cameras = getSceneState()?.cameras || {};
    const position = resolveContextPlacementPosition(null);
    const addedCameraUuid = sceneActions.addCamera({
      name: nextAvailableCameraName(cameras, { kind: "world" }),
      kind: "fixed",
      position,
      orientation: [1, 0, 0, 0],
      fovy: 65,
      width: 800,
      height: 800,
      fps: 8,
    });
    if (addedCameraUuid) {
      dispatchViewportCameraVisibility(true);
      focusNewContextEntity(selectionActions.selectCamera, addedCameraUuid);
    }
    closeContextMenu();
  };

  const handleContextAddCameraToBody = (parentBodyUuid) => {
    if (isPhysicsRunning || !parentBodyUuid) return;
    const cameras = getSceneState()?.cameras || {};
    const parentBody = safeBodies?.[parentBodyUuid];
    const position = resolveContextPlacementPosition(parentBodyUuid);
    const addedCameraUuid = sceneActions.addCamera({
      name: nextAvailableCameraName(cameras, {
        kind: "mounted_body",
        bodyName: parentBody?.name || String(parentBodyUuid),
      }),
      kind: "mounted",
      position,
      orientation: [1, 0, 0, 0],
      fovy: 65,
      width: 800,
      height: 800,
      fps: 8,
      mountType: "body",
      mountBodyUuid: parentBodyUuid,
    });
    if (addedCameraUuid) {
      dispatchViewportCameraVisibility(true);
      focusNewContextEntity(selectionActions.selectCamera, addedCameraUuid);
    }
    closeContextMenu();
  };

  const handleContextAddCameraToSite = (siteUuid) => {
    if (isPhysicsRunning || !siteUuid) return;
    const cameras = getSceneState()?.cameras || {};
    const site =
      Object.values(allSites || {}).find(
        (s) =>
          s && (String(s.uuid) === String(siteUuid) || String(s.name || "") === String(siteUuid))
      ) || null;
    const siteName = site?.name || site?.uuid || String(siteUuid);
    const parentBody = site
      ? Object.values(safeBodies || {}).find(
          (b) => Array.isArray(b?.sites) && b.sites.includes(site.uuid)
        )
      : null;
    const siteObject = findSiteObjectInViewerScene(sceneRef.current, site?.uuid || siteUuid);
    const position = resolveContextPlacementPositionForObject(siteObject);
    const addedCameraUuid = sceneActions.addCamera({
      name: nextAvailableCameraName(cameras, {
        kind: "mounted_site",
        siteName,
        ...(parentBody?.name ? { parentBodyName: parentBody.name } : {}),
      }),
      kind: "mounted",
      position,
      orientation: [1, 0, 0, 0],
      fovy: 65,
      width: 800,
      height: 800,
      fps: 8,
      mountType: "site",
      mountSiteUuid: siteUuid,
    });
    if (addedCameraUuid) {
      dispatchViewportCameraVisibility(true);
      focusNewContextEntity(selectionActions.selectCamera, addedCameraUuid);
    }
    closeContextMenu();
  };

  const handleContextAddCameraFromScenePick = (fallbackBodyUuid) => {
    if (isPhysicsRunning) return;
    const pick = sceneContextMenuData.priorityPick;
    if (pick?.targetType === "site" && pick.candidate?.name) {
      handleContextAddCameraToSite(String(pick.candidate.name));
      return;
    }
    if (pick?.targetType === "joint" && pick.candidate?.uuid) {
      const bodyUuid = findBodyForJointUuid(safeBodies, pick.candidate.uuid);
      if (bodyUuid) {
        handleContextAddCameraToBody(bodyUuid);
        return;
      }
    }
    if (pick?.targetType === "body" && pick.candidate?.uuid) {
      handleContextAddCameraToBody(String(pick.candidate.uuid));
      return;
    }
    if (pick?.targetType === "object" && pick.candidate?.uuid) {
      handleContextAddCameraToBody(String(pick.candidate.uuid));
      return;
    }
    if (fallbackBodyUuid) {
      handleContextAddCameraToBody(fallbackBodyUuid);
    }
  };

  const handleContextDuplicateBody = (sourceBodyUuid, includeBranch = false) => {
    if (isPhysicsRunning || !sourceBodyUuid) return;
    const sourceBody = safeBodies?.[sourceBodyUuid];
    if (!sourceBody) return;
    const newUuid = runDuplicateBodySubtreeGrouped(sceneActions, sourceBodyUuid, includeBranch);
    if (newUuid) {
      focusNewEntityInInspector("scene", () => selectionActions.selectBody(newUuid), {
        duplicateChromeFlash: true,
      });
    }
    closeContextMenu();
  };

  const findBodyForSiteUuidFromBodies = (siteUuid) => {
    if (!siteUuid) return null;
    const key = String(siteUuid);
    return (
      Object.values(safeBodies || EMPTY_OBJECT).find((body) =>
        (body?.sites || []).some((siteId) => String(siteId) === key)
      )?.uuid || null
    );
  };

  const findBodyForMeshUuidFromBodies = (meshUuid) => {
    if (!meshUuid) return null;
    const key = String(meshUuid);
    return (
      Object.values(safeBodies || EMPTY_OBJECT).find((body) =>
        (body?.geoms || []).some((geom) => String(geom?.mesh || "") === key)
      )?.uuid || null
    );
  };

  const resolveContextCandidateBodyUuid = (targetType, candidate) => {
    if (!candidate) return null;
    if (targetType === "body") return candidate.uuid || null;
    if (targetType === "object") return candidate.uuid || null;
    if (targetType === "joint") return findBodyForJointUuid(safeBodies || {}, candidate.uuid);
    if (targetType === "site") return findBodyForSiteUuidFromBodies(candidate.uuid);
    if (targetType === "mesh") {
      if (candidate.selectionType === "geom" && candidate.name) {
        const sep = String(candidate.name).indexOf(":");
        if (sep > 0) return String(candidate.name).slice(0, sep);
      }
      return findBodyForMeshUuidFromBodies(candidate.uuid);
    }
    if (targetType === "geom" || targetType === "inertial") {
      const key = candidate.name || candidate.uuid || "";
      const sep = String(key).indexOf(":");
      if (sep > 0) return String(key).slice(0, sep);
    }
    return null;
  };

  const priorityContextAddTargetBodyUuid = resolveContextCandidateBodyUuid(
    sceneContextMenuData.priorityPick?.targetType,
    sceneContextMenuData.priorityPick?.candidate
  );
  const contextAddTargetBodyUuid =
    priorityContextAddTargetBodyUuid ||
    sceneContextMenuData.candidates.body?.uuid ||
    sceneContextMenuData.owningBodyUuid ||
    null;

  return {
    contextAddTargetBodyUuid,
    handleContextAddBody,
    handleContextAddCameraFromScenePick,
    handleContextAddConstraint,
    handleContextAddFixedWorldCamera,
    handleContextAddGeomToBody,
    handleContextAddJointToBody,
    handleContextAddSiteToBody,
    handleContextAddTendon,
    handleContextAddVisualGeomToBody,
    handleContextClearScene,
    handleContextDeleteSelect,
    handleContextDuplicateBody,
    handleContextDuplicateCollision,
  };
}
